import type { CreatePriorAuthorizationPayload } from '../../services/priorAuthorizationService'
import type { PriorAuthDetailDocument } from './priorAuthTypes'

export type PriorAuthFieldErrors = Partial<Record<keyof CreatePriorAuthorizationPayload, string>>
export type PriorAuthDocumentErrors = Record<string, string>

const NPI_PATTERN = /^\d{10}$/
const HCPCS_PATTERN = /^[A-Z0-9]{4,5}$/i
const ICD10_PATTERN = /^[A-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/i

const ALLOWED_FILE_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

const ALLOWED_EXTENSIONS = /\.(pdf|jpe?g|png|docx?)$/i

const DOCUMENT_MAX_BYTES: Record<string, number> = {
  'doc-insurance': 5 * 1024 * 1024,
  'doc-referral': 10 * 1024 * 1024,
  'doc-records': 10 * 1024 * 1024,
  'doc-prior-treatment': 10 * 1024 * 1024,
  'doc-lab-imaging': 10 * 1024 * 1024,
  'doc-lmn': 10 * 1024 * 1024,
}

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024

export const hasValidationErrors = (errors: Record<string, string | undefined>): boolean =>
  Object.values(errors).some((message) => Boolean(message?.trim()))

export function validatePatientStep(form: CreatePriorAuthorizationPayload): PriorAuthFieldErrors {
  const errors: PriorAuthFieldErrors = {}

  if (!form.request_number.trim()) {
    errors.request_number = 'Request number is required'
  }
  if (!form.patient_id.trim()) {
    errors.patient_id = 'Patient is required'
  }
  if (!form.payor_id.trim()) {
    errors.payor_id = 'Payor is required'
  }
  if (!form.subscriber_id.trim()) {
    errors.subscriber_id = 'Member ID is required — select a patient with coverage data'
  }

  const npi = form.provider_npi.trim()
  if (npi && !NPI_PATTERN.test(npi)) {
    errors.provider_npi = 'NPI must be exactly 10 digits'
  }

  return errors
}

export function validateClinicalStep(form: CreatePriorAuthorizationPayload): PriorAuthFieldErrors {
  const errors: PriorAuthFieldErrors = {}

  const serviceCode = form.service_code.trim()
  if (!serviceCode) {
    errors.service_code = 'Service code is required'
  } else if (!HCPCS_PATTERN.test(serviceCode)) {
    errors.service_code = 'Enter a valid HCPC/CPT code (4–5 characters)'
  }

  const diagnosisCode = form.diagnosis_code.trim()
  if (!diagnosisCode) {
    errors.diagnosis_code = 'Diagnosis code is required'
  } else if (!ICD10_PATTERN.test(diagnosisCode)) {
    errors.diagnosis_code = 'Enter a valid ICD-10 code (e.g. J44.1)'
  }

  if (!form.service_description.trim()) {
    errors.service_description = 'Service description is required'
  }

  if (!form.requested_service_date) {
    errors.requested_service_date = 'Requested service date is required'
  }

  if (!form.submitted_date) {
    errors.submitted_date = 'Submitted date is required'
  } else if (
    form.requested_service_date &&
    form.submitted_date > form.requested_service_date
  ) {
    errors.submitted_date = 'Submitted date cannot be after requested service date'
  }

  return errors
}

export function validatePriorAuthDocumentFile(docId: string, file: File): string | null {
  const name = file.name.toLowerCase()
  const typeOk =
    (file.type && ALLOWED_FILE_TYPES.has(file.type)) || ALLOWED_EXTENSIONS.test(name)
  if (!typeOk) {
    return 'File must be PDF, JPG, PNG, DOC, or DOCX'
  }

  const maxBytes = DOCUMENT_MAX_BYTES[docId] ?? DEFAULT_MAX_BYTES
  if (file.size > maxBytes) {
    const maxMb = maxBytes / (1024 * 1024)
    return `File exceeds ${maxMb} MB limit`
  }

  return null
}

export function validateDocumentsStep(documents: PriorAuthDetailDocument[]): PriorAuthDocumentErrors {
  const errors: PriorAuthDocumentErrors = {}

  for (const doc of documents) {
    if (doc.required && !doc.uploaded) {
      errors[doc.id] = `${doc.name} is required`
      continue
    }
    if (doc.uploaded && doc.file instanceof File) {
      const fileError = validatePriorAuthDocumentFile(doc.id, doc.file)
      if (fileError) errors[doc.id] = fileError
    }
  }

  return errors
}

export function validatePriorAuthCreate(
  form: CreatePriorAuthorizationPayload,
  documents: PriorAuthDetailDocument[]
): { fieldErrors: PriorAuthFieldErrors; documentErrors: PriorAuthDocumentErrors } {
  return {
    fieldErrors: {
      ...validatePatientStep(form),
      ...validateClinicalStep(form),
    },
    documentErrors: validateDocumentsStep(documents),
  }
}
